/**
 * Keys that are valid inside `presentation: { ... }` on every entity that has
 * a presentation layer. Per-entity policies extend this with entity-specific
 * keys (child ordering, etc.).
 */
const BASE_PRESENTATION = ['name', 'hidden'];
/**
 * `brandLevelId` links a location entity to its brand-level template. Not
 * POS-managed and not user-changed, but Hermes serializes it on every save
 * because the form is bound to the entity's full shape. Allowing it as a
 * native pass-through avoids spurious rejections without compromising POS
 * data safety.
 */
const COMMON_NATIVE = ['brandLevelId'];
const makePolicy = (extra) => ({
    native: new Set([...COMMON_NATIVE, ...(extra.native ?? [])]),
    presentation: new Set([...BASE_PRESENTATION, ...(extra.presentation ?? [])]),
});
const ITEM_OTTER = makePolicy({
    native: [
        'points',
        'alcohol',
        'popular',
        'upsell',
        'taxable',
        'excludeFromThrottle',
        'channels',
    ],
    presentation: ['description', 'image', 'modifierSets'],
});
const ITEM_CLOVER = makePolicy({
    // Clover items expose richer per-item metadata that Otter doesn't.
    native: [
        'points',
        'alcohol',
        'popular',
        'upsell',
        'taxable',
        'excludeFromThrottle',
        'channels',
        'glutenFree',
        'vegetarian',
        'allergens',
        'nutritionalInformation',
        'variationGroups',
    ],
    presentation: ['description', 'image', 'modifierSets'],
});
const CATEGORY_BOTH = makePolicy({
    native: ['hours', 'upsellCategories', 'excludeFromThrottle', 'channels'],
    presentation: ['description', 'items'],
});
// Modifier description/image are Owner-owned (not POS-controlled), so they're
// native. `modifierSets` (nested option groups) is overridable for Clover;
// Otter modifiers never carry nested groups.
const MODIFIER_COMMON_NATIVE = [
    'description',
    'image',
    'topping',
    'min',
    'max',
    'taxable',
];
const MODIFIER_PRESENTATION = ['modifierSets'];
const MODIFIER_OTTER = makePolicy({
    native: [...MODIFIER_COMMON_NATIVE, 'alcohol'],
    presentation: MODIFIER_PRESENTATION,
});
/**
 * Clover alone permits the dietary flags and `allergens`: Otter and Uber both
 * re-derive them on every resync, writing the empty value when the POS sends
 * nothing, so a write here would record an allergen the next sync drops.
 */
const MODIFIER_CLOVER = makePolicy({
    native: [
        ...MODIFIER_COMMON_NATIVE,
        'alcohol',
        'glutenFree',
        'vegetarian',
        'allergens',
    ],
    presentation: MODIFIER_PRESENTATION,
});
// `min`/`max`/`toppings` are POS-synced and stay blocked; operators wanting to
// change those must do so in the POS. `displayType` is also blocked even though
// the POS sync doesn't import it: both Otter and Clover reactively rewrite it
// when synced min/max bounds contradict the current type, and the editor can't
// touch the (locked) min/max to keep the two consistent. Hermes hides the type
// control on POS modifier sets for the same reason.
const MODIFIER_SET_OTTER = makePolicy({
    native: ['channels'],
    presentation: ['modifiers'],
});
const MODIFIER_SET_CLOVER = makePolicy({
    native: ['channels', 'preselectedModifiers'],
    presentation: ['modifiers'],
});
const MENU_BOTH = makePolicy({
    native: ['type', 'allowInstructions', 'channels'],
    presentation: ['categories'],
});
/**
 * Coupons aren't POS-synced (their `pos` is always null), so the gate will
 * short-circuit before this policy is consulted. Listed with empty allowlists
 * to satisfy the `Record<MenuEntityCollection, …>` shape.
 */
const EMPTY = makePolicy({});
/**
 * Uber alone permits `amount`, because only its sync honours `priceOverridden` and
 * skips the field. Granting it to Otter or Clover would let an operator price an
 * entity their next sync silently reverts. `posAmount` is sync-owned and stays out.
 */
const PRICE_OVERRIDE_NATIVE = ['amount', 'priceOverridden'];
/**
 * Uber's one subtraction from Otter's allowlists. Every Uber policy must go
 * through this — `convertUberItem` hard-codes `alcohol: false`.
 */
const withoutAlcohol = (fields) => [...fields].filter((field) => field !== 'alcohol');
const ITEM_UBER = makePolicy({
    native: [...withoutAlcohol(ITEM_OTTER.native), ...PRICE_OVERRIDE_NATIVE],
    presentation: [...ITEM_OTTER.presentation],
});
const MODIFIER_UBER = makePolicy({
    native: [...withoutAlcohol(MODIFIER_OTTER.native), ...PRICE_OVERRIDE_NATIVE],
    presentation: [...MODIFIER_OTTER.presentation],
});
/** Uber otherwise reuses Otter's allowlists — same payload shape, same overrides. */
export const POS_FIELD_POLICY = {
    Item: { otter: ITEM_OTTER, clover: ITEM_CLOVER, uber: ITEM_UBER },
    Category: {
        otter: CATEGORY_BOTH,
        clover: CATEGORY_BOTH,
        uber: CATEGORY_BOTH,
    },
    Modifier: {
        otter: MODIFIER_OTTER,
        clover: MODIFIER_CLOVER,
        uber: MODIFIER_UBER,
    },
    ModifierSet: {
        otter: MODIFIER_SET_OTTER,
        clover: MODIFIER_SET_CLOVER,
        uber: MODIFIER_SET_OTTER,
    },
    Menu: { otter: MENU_BOTH, clover: MENU_BOTH, uber: MENU_BOTH },
    Coupon: { otter: EMPTY, clover: EMPTY, uber: EMPTY },
};
//# sourceMappingURL=policy.js.map